import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import ControlledCheckBox from 'src/shared/components/form/controlledCheckbox';
import { FundraiserSettingData } from '../models/fundraiserResponse';

type FundraiserDetailSettingsSectionProps = {
  settingData: FundraiserSettingData;
};

export const FundraiserSettingsDetailSection = (props: FundraiserDetailSettingsSectionProps) => {
  const { settingData } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [, setLoading] = useState<boolean>(true);

  const fundraiserSettingsSchema = yup.object().shape({
    customHeading: yup.boolean().required(),
    disallowAnonymous: yup.boolean().required(),
    displayAddressFields: yup.boolean().required(),
    displayMessageField: yup.boolean().required(),
    publicUrl: yup.string().optional(),
    destinationList: yup.string().optional(),
    mainHeadingVisible: yup.boolean().required(),
    isAdminForm: yup.boolean().required(),
    displayInvoiceInPayment: yup.string().oneOf(['0', '1']).required(),
  });

  const formMethods = useForm<FundraiserSettingData>({
    resolver: yupResolver(fundraiserSettingsSchema),
    defaultValues: {
      customHeading: settingData.customHeading,
      disallowAnonymous: settingData.disallowAnonymous,
      displayAddressFields: settingData.displayAddressFields,
      displayMessageField: settingData.displayMessageField,
      publicUrl: settingData.publicUrl || '',
      destinationList: settingData.destinationList || '',
      mainHeadingVisible: settingData.mainHeadingVisible,
      isAdminForm: settingData.isAdminForm,
      displayInvoiceInPayment: settingData.displayInvoiceInPayment ? '1' : '0',
    },
  });

  const { handleSubmit, watch } = formMethods;
  const invInPay = watch('displayInvoiceInPayment');

  const onSubmit = (data: FundraiserSettingData) => {
    setLoading(true);

    // Mappa tillbaka till boolean innan post
    const dataToPost: FundraiserSettingData = {
      ...data,
      displayInvoiceInPayment: data.displayInvoiceInPayment === '1',
    };
    ConsoleHelper.log('dataToPost: ', dataToPost);

    try {
      //   const res = await updateFundraiserSettings(data);
      if (isMounted()) {
        toast.success(<>{t('fundraiser:fundraiserDetailSettingsSection.postSuccess')}</>);
      }
    } catch (e: any) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('fundraiser:fundraiserDetailSettingsSection.postError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
              {t('fundraiser:fundraiserDetailSettingsSection.settings')}
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 600, mt: 2 }} gutterBottom>
              {t('fundraiser:fundraiserDetailSettingsSection.formAndPayment')}
            </Typography>
            <ControlledCheckBox
              clickOnlyOnBox
              disabled
              label={t('fundraiser:fundraiserDetailSettingsSection.displayInvoiceInPayment')}
              name='displayInvoiceInPayment'
              value={invInPay}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
